import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { detailedServicesData } from '../../mocks/detailed-services';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = detailedServicesData.find((s) => s.slug === slug);

  useEffect(() => {
    if (!service) return;
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Set page title and meta tags
    document.title = `${service.title} | Es Donanım Bilgisayar Ankara`;
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', service.description);
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', `${service.title}, ${service.category}, bilgisayar servisi ankara, teknik servis`);
    }
    
    // Set canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}/hizmet/${service.slug}`);
    
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": service.title,
      "name": service.title,
      "description": service.description,
      "provider": {
        "@type": "LocalBusiness",
        "name": "Es Donanım Bilgisayar",
        "telephone": "+905065376985",
        "email": "info@esbilgisayar.com.tr",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Anafartalar Cad. Ulus Şehir Çarşısı No:3/11",
          "addressLocality": "Altındağ",
          "addressRegion": "Ankara",
          "postalCode": "06030",
          "addressCountry": "TR"
        }
      },
      "areaServed": {
        "@type": "City",
        "name": "Ankara"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock"
      }
    });
    document.head.appendChild(schemaScript);
    
    return () => {
      document.head.removeChild(schemaScript);
    };
  }, [service]);

  if (!service) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pb-16 lg:pb-0">
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <i className="ri-error-warning-line text-6xl text-gray-400 mb-4"></i>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Hizmet Bulunamadı</h1>
              <p className="text-gray-600 mb-6">Aradığınız hizmet mevcut değil.</p>
              <Link
                to="/hizmetler"
                className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                Hizmetlere Dön
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const otherServices = detailedServicesData
    .filter((s) => s.id !== service.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <WhatsAppButton />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-orange-500 transition-colors">
              Ana Sayfa
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/hizmetler" className="hover:text-orange-500 transition-colors">
              Hizmetler
            </Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">{service.title}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/30 rounded-full text-orange-400 text-sm font-medium mb-6">
                <i className={`${service.icon} text-lg`}></i>
                <span>Profesyonel Hizmet</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                {service.title}
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                {service.shortDesc}
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+905065376985"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all hover:scale-105 font-semibold whitespace-nowrap"
                >
                  <i className="ri-phone-line text-xl"></i>
                  Hemen Ara
                </a>
                <a
                  href="https://wa.me/905065376985"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all hover:scale-105 font-semibold whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  WhatsApp
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-blue-500/20 rounded-2xl blur-2xl"></div>
              <img
                src={service.image}
                alt={service.title}
                className="relative rounded-2xl shadow-2xl w-full h-96 object-cover border-4 border-white/10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Service Details */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 rounded-2xl p-8 mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Hizmet Detayları</h2>
                <p className="text-lg text-gray-700 leading-relaxed mb-8">
                  {service.description}
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mb-6">Hizmet Özellikleri</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {service.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-orange-500 transition-colors"
                    >
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-fill text-xl text-orange-500"></i>
                      </div>
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Process Steps */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-8">Hizmet Süreci</h3>
                <div className="space-y-6">
                  {[
                    { step: '1', title: 'İletişim', desc: 'Telefon veya WhatsApp ile bize ulaşın' },
                    { step: '2', title: 'Değerlendirme', desc: 'Uzman ekibimiz ücretsiz ön değerlendirme yapar' },
                    { step: '3', title: 'Teklif', desc: 'Şeffaf ve net fiyat teklifi sunulur' },
                    { step: '4', title: 'İşlem', desc: 'Hızlı ve profesyonel hizmet gerçekleştirilir' },
                    { step: '5', title: 'Teslimat', desc: 'Garanti belgesi ile birlikte teslim edilir' },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-orange-500 rounded-full text-xl font-bold">
                        {item.step}
                      </div>
                      <div className="flex-1 pt-2">
                        <h4 className="text-lg font-bold mb-1">{item.title}</h4>
                        <p className="text-gray-300">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Contact Card */}
              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 text-white mb-8 sticky top-24">
                <div className="text-center mb-6">
                  <i className="ri-customer-service-2-line text-6xl mb-4 opacity-90"></i>
                  <h3 className="text-2xl font-bold mb-2">Hemen Teklif Alın</h3>
                  <p className="text-orange-100">Ücretsiz danışmanlık için bize ulaşın</p>
                </div>

                <div className="space-y-4">
                  <a
                    href="tel:+905065376985"
                    className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-white text-orange-600 rounded-lg hover:bg-orange-50 transition-colors font-semibold whitespace-nowrap"
                  >
                    <i className="ri-phone-line text-xl"></i>
                    0506 537 69 85
                  </a>
                  <a
                    href="https://wa.me/905065376985"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line text-xl"></i>
                    WhatsApp
                  </a>
                  <Link
                    to="/iletisim"
                    className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-semibold whitespace-nowrap"
                  >
                    <i className="ri-mail-line text-xl"></i>
                    İletişim Formu
                  </Link>
                </div>

                <div className="mt-8 pt-8 border-t border-orange-400/30">
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center gap-3">
                      <i className="ri-time-line text-xl"></i>
                      <span>Pazartesi - Cumartesi: 09:00 - 19:00</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-map-pin-line text-xl"></i>
                      <span>Anafartalar Cad. Ulus Şehir Çarşısı No:3/11 Altındağ / ANKARA</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-shield-check-line text-xl"></i>
                      <span>12 Ay Garanti</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Other Services */}
      {otherServices.length > 0 && (
        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Diğer Hizmetlerimiz
              </h2>
              <p className="text-lg text-gray-600">
                Size uygun diğer hizmetlerimize göz atın
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {otherServices.map((otherService) => (
                <Link
                  key={otherService.id}
                  to={`/hizmetler/${otherService.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={otherService.image}
                      alt={otherService.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-lg">
                        <i className={`${otherService.icon} text-2xl text-white`}></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-orange-500 transition-colors">
                      {otherService.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {otherService.shortDesc}
                    </p>
                    <div className="flex items-center gap-2 text-orange-500 font-semibold">
                      <span>Detaylı Bilgi</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default ServiceDetailPage;